/* Claude 4.71 — DRAWER UPSELLS TIED TO THE BASKET OFFER (Option C, Nov 2026)
 * Claude 4.74 — BEST DEAL WINS (BOGO25 or BASKET10) + MARGIN GUARD + SHORT NAMES
 * Claude 4.76 — BOGO25 OFF (BOGO_ON = false): BASKET10 only, see BOGO_ON below
 *
 * WHAT IT DOES. Up to 3 recommendations, always open, in a tinted box right
 * under the basket lines, each tied to the basket's best offer.
 *
 * 4.74 — BEST DEAL WINS. Two codes can be on offer and they never combine:
 *   BOGO25   — buy 1 get 1 at 25% off. No per-order limit: every pair gets
 *              25% off its cheaper item (Shopify discounts the cheapest units).
 *   BASKET10 — 10% off everything from £700 (code + minimum come from the
 *              drawer's data-autoapply-code / data-autoapply-min, i.e. theme
 *              settings, exactly as before).
 * The drawer works out what each would save on the real basket and applies
 * the bigger one itself (it replaces maybeAutoApply below). A code the
 * shopper typed is never touched, and one they removed is not re-added.
 *
 * MARGIN GUARD. BOGO25 is only applied/pitched when every unit that would get
 * the 25% costs at least GUARD_MIN (£79) and is not already on sale
 * (compare-at price above price). Otherwise BOGO25 counts as "no saving".
 *
 * THE PANEL (renderOfferUpsells), in this order:
 *   1. BASKET10 already applied  → "Complete the look", prices only, and the
 *                                   top tier bar shows ("10% off applied").
 *   2. Within NEAR_GAP (£150) of the minimum AND 10% there beats today's
 *      saving                    → "£152 to 10% off everything" + progress;
 *                                   rows say "gets you 10% off: save £74.70"
 *                                   or "then £17 to 10% off".
 *   3. A suggestion would add a BOGO25 saving
 *                                 → "Add a 2nd bag — 25% off the cheaper one";
 *                                   rows show £135 → £101.25 · save £33.75.
 *   4. Otherwise                  → "Complete the look", prices only.
 *
 * SHORT NAMES. Basket lines and suggestions show short names
 * ("Margot Crossbody", "Double Sheepskin Rug"); the full title stays in the
 * link's title attribute. See shortName().
 *
 * BUTTON (4.74, option A1): 26px solid black square with a white plus, inside
 * a 44px tap area.
 *
 * WHY A SEPARATE FILE. assets/claude-cart-drawer.js is ~51KB, so this patches
 * the drawer's prototype instead of retyping it. Methods changed:
 *   renderCtlRows(), paintCtl()   (4.71, unchanged)
 *   maybeAutoApply()              (4.74, replaced: best deal wins)
 *   removeDiscount()              (4.74, wrapped: remembers a removed code)
 * IF claude-cart-drawer.js IS EVER REWRITTEN, fold this in and delete this
 * file, its stylesheet and both tags in sections/claude-cart-drawer.liquid.
 */
(() => {
  const ADDON_FALLBACK = ['luxury-gift-box', 'cardboard-package', 'tracked-shipping'];
  const BOGO_CODE = 'BOGO25';
  const BOGO_PCT = 25;
  const GUARD_MIN = 7900; /* pence: cheapest discounted unit must cost at least £79 */
  const NEAR_GAP = 15000; /* pence: pitch the 10% only when within £150 of it (BOGO on) */
  /* 4.76 — BOGO25 switched OFF in the drawer (owner, 3 Nov 2026): not applied,
     not pitched, and a BOGO25 the shopper types is left alone. The drawer is
     back to BASKET10 only. Set to true to bring best-deal-wins back. */
  const BOGO_ON = false;
  /* 4.76 — the "£X to 10% off everything" pitch only shows once the basket
     (real products, packaging/delivery excluded) reaches £250 (owner). */
  const TEN_FROM = 25000;
  const PLUS_ICON =
    '<span class="ccd-oups-plus" aria-hidden="true"><svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M6 1v10M1 6h10"/></svg></span>';

  const money = (cents) => {
    cents = Math.round(cents);
    const pounds = Math.floor(cents / 100).toLocaleString('en-GB');
    const pence = Math.round(cents % 100);
    return pence === 0 ? `£${pounds}` : `£${pounds}.${String(pence).padStart(2, '0')}`;
  };
  const esc = (s) =>
    String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const pctOf = (code) => {
    const m = String(code || '').match(/\d+/);
    return m ? parseInt(m[0], 10) : 0;
  };
  const lower = (s) => String(s || '').toLowerCase();

  /* ---------- short names ----------
     Clemés bags: first name + bag type ("Margot Crossbody"). Raffia and a
     size in brackets (Midi, Mini…) are kept because they tell same-named
     bags apart. Everything else: the part before the first comma, with
     filler words dropped ("Double Sheepskin Rug", "Cowhide Cushion Cover"). */
  const BAG_TYPES = ['Shoulder Bag', 'Crossbody', 'Tote', 'Hobo', 'Backpack', 'Clutch', 'Handbag', 'Bucket Bag', 'Bucket', 'Bowling Bag', 'Saddle Bag', 'Satchel', 'Wallet', 'Purse', 'Bag'];
  const SIZE_WORDS = /^(midi|mini|maxi|small|medium|large|xl)$/i;
  const FILLER = /\b(genuine|natural|premium|real|new zealand|mongolian)\b/gi;
  const SIZE_IN = /\b(Single|Double|Quad|Sexto|Octo|Rectangular|Small|Medium|Large|XXL|XL)\b/;
  const shortName = (title) => {
    let t = String(title || '').trim();
    if (!t) return t;
    if (/\bby clem[eé]s\b/i.test(t)) {
      let size = '';
      let colour = '';
      t = t.replace(/\(([^)]*)\)/g, (m, inner) => {
        inner = inner.trim();
        if (SIZE_WORDS.test(inner)) size = inner;
        else if (!/\d/.test(inner) && !/\b(sizes?|colou?rs?)\b/i.test(inner)) colour = inner; /* "(Tan)" */
        return '';
      });
      t = t.replace(/,?\s*\bby clem[eé]s\b/i, '').replace(/\s+/g, ' ').trim();
      /* "Milano Suede Bag, Olive": each colour is its own product, so a
         comma part (the colour) is kept: "Milano Bag, Olive". */
      const commaParts = t.split(',').map((s) => s.trim()).filter(Boolean);
      const extra = commaParts.slice(1).concat(colour ? [colour] : []).join(', ');
      t = commaParts[0] || t;
      const first = t.split(' ')[0];
      const raffia = /\braffia\b/i.test(t) ? 'Raffia' : '';
      let type = '';
      let at = -1;
      BAG_TYPES.forEach((bt) => {
        const re = new RegExp(`\\b${bt}\\b`, 'gi');
        let m;
        let i = -1;
        while ((m = re.exec(t))) i = m.index;
        if (i > at || (i > -1 && i === at && bt.length > type.length)) { at = i; type = bt; }
      });
      if (/\b(tote|crossbody|hobo|clutch|backpack|satchel)\s+bag\b/i.test(t)) {
        type = t.match(/\b(tote|crossbody|hobo|clutch|backpack|satchel)\s+bag\b/i)[1];
        type = type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
      }
      if (type === 'Bag' && /shoulder bag/i.test(t)) type = 'Shoulder Bag';
      const name = [first, size, raffia, type || 'Bag'].filter(Boolean).join(' ');
      return extra ? `${name}, ${extra}` : name;
    }
    if (t.length > 40 && t.indexOf(',') > -1) {
      const parts = t.split(',');
      let head = parts[0]
        .replace(/\s*&\s*throw\b/i, '')
        .replace(/\/throw\b/i, '')
        .replace(FILLER, '')
        .replace(/\s+/g, ' ')
        .trim();
      /* keep the size: if the head has none, take the first one after it */
      if (!SIZE_IN.test(head)) {
        const m = parts.slice(1).join(',').match(SIZE_IN);
        if (m) head = `${m[1]} ${head}`;
      }
      if (head.length >= 6) return head;
    }
    if (t.length > 40) {
      /* "Pair of Natural Premium New Zealand Sheepskin Cushions 35 x 35 cm
         (Jungle Green)" → "Pair of Sheepskin Cushions 35 x 35 cm, Jungle Green" */
      let colour = '';
      let s = t.replace(/\(([^)]*)\)/g, (m, inner) => {
        if (!/\d/.test(inner)) colour = inner.trim();
        return '';
      });
      s = s.replace(FILLER, '').replace(/\s+/g, ' ').trim();
      if (s.length >= 6) return colour ? `${s}, ${colour}` : s;
    }
    return t;
  };
  const shortenTitles = (root) => {
    if (!root) return;
    root.querySelectorAll('.ccd-item-title').forEach((a) => {
      if (a.dataset.short === '1' || a.children.length) return;
      const full = a.textContent.trim();
      let s = shortName(full);
      /* 4.76 — don't repeat the colour: "Giannina Tote, Tan" over a "Tan"
         variant line becomes "Giannina Tote". */
      const varEl = a.parentNode && a.parentNode.querySelector('.ccd-variant');
      const vt = varEl ? varEl.textContent.trim().toLowerCase() : '';
      if (vt) {
        const parts = s.split(', ');
        if (parts.length > 1 && vt.indexOf(parts[parts.length - 1].toLowerCase()) > -1) s = parts.slice(0, -1).join(', ');
      }
      a.dataset.short = '1';
      if (s && s !== full) {
        a.title = full;
        a.textContent = s;
      }
    });
  };

  /* ---------- sale lookup (for the margin guard) ---------- */
  const saleCache = {};
  const variantOnSale = async (handle, variantId) => {
    if (!handle) return false;
    if (!saleCache[handle]) {
      saleCache[handle] = fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null);
    }
    const p = await saleCache[handle];
    if (!p || !p.variants) return false;
    const v = p.variants.find((x) => String(x.id) === String(variantId)) || p.variants[0];
    return !!(v && v.compare_at_price && v.compare_at_price > v.price);
  };

  /* 4.74 — the variant a suggestion offers: best-selling IN-STOCK variant,
     worked out in Liquid by templates/product.claude-bestvariant.liquid. */
  const bestCache = {};
  const bestVariant = (p) => {
    const path = String((p && p.url) || '').split('?')[0];
    if (!path) return Promise.resolve(null);
    if (!bestCache[path]) {
      /* No Accept: application/json header — with it Shopify answers with its
         own product JSON instead of rendering the alternate template. */
      bestCache[path] = fetch(`${path}?view=claude-bestvariant`, { credentials: 'same-origin' })
        .then((r) => (r.ok ? r.text() : ''))
        .then((t) => {
          try {
            const j = JSON.parse(t);
            return j && j.id ? j : null;
          } catch (e) { return null; }
        })
        .catch(() => null);
    }
    return bestCache[path];
  };

  /* Units = one entry per item, add-ons (packaging, tracked delivery) excluded. */
  const cartUnits = async (cart, handles) => {
    const units = [];
    for (const it of (cart && cart.items) || []) {
      if (handles.indexOf(it.handle) > -1) continue;
      const sale = await variantOnSale(it.handle, it.variant_id);
      const price = it.original_price != null ? it.original_price : it.price;
      for (let q = 0; q < it.quantity; q++) units.push({ price, sale, type: it.product_type || '' });
    }
    return units;
  };

  /* What each code would save. BOGO: sort high→low, the cheapest floor(n/2)
     units get 25% off — blocked entirely if any of them fails the guard. */
  const bogoSave = (units) => {
    if (!BOGO_ON) return 0;
    const sorted = units.slice().sort((a, b) => b.price - a.price);
    const n = Math.floor(sorted.length / 2);
    if (!n) return 0;
    const disc = sorted.slice(sorted.length - n);
    if (disc.some((u) => u.sale || u.price < GUARD_MIN)) return 0;
    return disc.reduce((s, u) => s + Math.round((u.price * BOGO_PCT) / 100), 0);
  };
  const basketSave = (units, code, min) => {
    if (!code || !min) return 0;
    const total = units.reduce((s, u) => s + u.price, 0);
    return total >= min ? Math.round((total * pctOf(code)) / 100) : 0;
  };
  const bestDeal = (units, code, min) => {
    const b = bogoSave(units);
    const k = basketSave(units, code, min);
    if (k > 0 && k >= b) return { code, save: k };
    if (b > 0) return { code: BOGO_CODE, save: b };
    return { code: '', save: 0 };
  };

  const patch = (Drawer) => {
    const proto = Drawer.prototype;
    if (proto.__ccdOfferUpsells) return false;
    proto.__ccdOfferUpsells = true;

    const dismissed = new Set();

    proto.renderCtlRows = async function (panel) {
      if (panel) panel.innerHTML = '';
    };

    const origPaintCtl = proto.paintCtl;
    proto.paintCtl = function () {
      const out = origPaintCtl.apply(this, arguments);
      const wrap = this.querySelector('[data-ccd-ctl-wrap]');
      if (wrap) {
        const btn = wrap.querySelector('[data-ccd-ctl-toggle]');
        const hasOffer = !!wrap.querySelector('[data-ccd-ctl-panel] .ccd-orow--pack');
        if (btn) btn.hidden = !hasOffer;
        if (!hasOffer) {
          const outer = wrap.querySelector('[data-ccd-ctl-panel]');
          if (outer) outer.hidden = true;
        }
      }
      shortenTitles(this);
      this.renderOfferUpsells();
      return out;
    };

    /* ---- best deal wins: replaces the drawer's own auto-apply ---- */
    const origRemove = proto.removeDiscount;
    proto.removeDiscount = function (code) {
      const inner = this.querySelector('#ClaudeCartDrawer-Inner');
      const ours = [BOGO_ON && BOGO_CODE, inner && inner.dataset.autoapplyCode].filter(Boolean).map(lower);
      if (ours.indexOf(lower(code)) > -1) dismissed.add(lower(code));
      return origRemove.apply(this, arguments);
    };

    proto.maybeAutoApply = async function () {
      if (this._autoBusy) return;
      const inner = this.querySelector('#ClaudeCartDrawer-Inner');
      if (!inner || inner.dataset.itemCount === '0') return;
      const baseCode = inner.dataset.autoapplyCode || '';
      const baseMin = parseInt(inner.dataset.autoapplyMin || '0', 10);
      const managed = [baseCode, BOGO_ON && BOGO_CODE].filter(Boolean).map(lower);
      const isOurs = (c) => managed.indexOf(lower(c)) > -1;
      const update = (window.routes && window.routes.cart_update_url) || '/cart/update';
      this._autoBusy = true;
      try {
        const cart = await this.fetchCart();
        if (!cart) return;
        const handles = typeof this.addonHandles === 'function' ? this.addonHandles() : ADDON_FALLBACK;
        const units = await cartUnits(cart, handles);
        const target = bestDeal(units, baseCode, baseMin).code;
        const targetLower = lower(target);
        const entries = cart.discount_codes || [];
        const codes = entries.map((d) => d.code);
        const stateKey = `${cart.item_count}:${cart.original_total_price}`;
        const mine = entries.filter((d) => isOurs(d.code));
        const theirs = entries.filter((d) => !isOurs(d.code));
        const drop = (cl) => this.cartRequest(update, { discount: codes.filter((c) => lower(c) !== cl).join(',') });
        const rejected = mine.find((d) => d.applicable === false);
        if (rejected) {
          this._rejectedState = `${stateKey}:${lower(rejected.code)}`;
          await drop(lower(rejected.code));
          return;
        }
        const wrong = mine.find((d) => lower(d.code) !== targetLower);
        if (wrong) {
          await drop(lower(wrong.code));
          return;
        }
        if (!target || mine.length || theirs.length || dismissed.has(targetLower) || this._rejectedState === `${stateKey}:${targetLower}`) return;
        await this.cartRequest(update, { discount: codes.concat(target).join(',') });
      } finally {
        this._autoBusy = false;
      }
    };

    /* ---- the panel ---- */
    proto.renderOfferUpsells = async function () {
      const inner = this.querySelector('#ClaudeCartDrawer-Inner');
      if (!inner || inner.dataset.itemCount === '0') return;
      const anchor = inner.querySelector('[data-ccd-ctl-wrap]');
      const sourceId = inner.dataset.sourceProduct;
      if (!anchor || !sourceId) return;

      const token = (this._oupsToken = (this._oupsToken || 0) + 1);
      let box = inner.querySelector('.ccd-oups');
      if (!box) {
        box = document.createElement('div');
        box.className = 'ccd-oups';
        box.hidden = true;
        anchor.parentNode.insertBefore(box, anchor);
      }

      let inCart = [];
      try { inCart = JSON.parse(inner.dataset.cartProducts || '[]'); } catch (e) { /* noop */ }

      if (!this.upsellCache) this.upsellCache = {};
      let products = this.upsellCache[sourceId];
      if (!products) {
        try {
          products = await this.fetchRecommendations(sourceId, 'complementary');
          if (!products.length) products = await this.fetchRecommendations(sourceId, 'related');
          this.upsellCache[sourceId] = products;
        } catch (e) { return; }
      }
      const cart = typeof this.fetchCart === 'function' ? await this.fetchCart() : null;
      const handles = typeof this.addonHandles === 'function' ? this.addonHandles() : ADDON_FALLBACK;
      const units = cart ? await cartUnits(cart, handles) : [];
      if (token !== this._oupsToken || !box.isConnected) return;

      /* 4.74 — each suggestion is ONE in-stock variant (best seller first, see
         templates/product.claude-bestvariant.liquid), so + always adds it
         directly; products with nothing in stock are skipped. */
      const pool = (products || []).filter((p) => p.available && !inCart.includes(p.id)).slice(0, 6);
      const picked = await Promise.all(pool.map((p) => bestVariant(p)));
      if (token !== this._oupsToken || !box.isConnected) return;
      const items = pool.map((p, i) => ({ p, v: picked[i] })).filter((x) => x.v).slice(0, 3);
      const topBar = inner.querySelector('.ccd-tierbar');
      if (!items.length) {
        box.hidden = true;
        if (topBar) topBar.hidden = false;
        return;
      }

      const baseCode = inner.dataset.autoapplyCode || '';
      const baseMin = parseInt(inner.dataset.autoapplyMin || '0', 10);
      const basePct = pctOf(baseCode);
      const real = units.reduce((s, u) => s + u.price, 0);
      const now = bestDeal(units, baseCode, baseMin);
      const allBags = units.length > 0 && units.every((u) => /bag/i.test(u.type));
      const noun = allBags ? 'bag' : 'item';

      const withP = items.map(({ p, v }) => {
        const sale = !!(v.compare_at_price && v.compare_at_price > v.price);
        const u = { price: v.price, sale, type: p.type || '' };
        const after = units.concat([u]);
        return { p, v, sale, deal: bestDeal(after, baseCode, baseMin), bogo: bogoSave(after) };
      });

      const gap = baseMin - real;
      let mode = 'plain';
      if (baseCode && now.code === baseCode) mode = 'done';
      else if (baseCode && gap > 0 && (BOGO_ON ? gap <= NEAR_GAP : real >= TEN_FROM) && Math.round((baseMin * basePct) / 100) > now.save) mode = 'basket';
      else if (withP.some((w) => w.bogo > bogoSave(units))) mode = 'bogo';

      const toggle = inner.querySelector('[data-ccd-ctl-toggle]');
      const fallbackHeading = (toggle && toggle.firstChild && toggle.firstChild.textContent.trim()) || 'Complete the look';

      let heading = fallbackHeading;
      let note = '';
      if (mode === 'basket') {
        heading = `${money(gap)} to ${basePct}% off everything`;
        if (now.save > 0) note = `That beats your current ${money(now.save)} saving — we'll switch it for you`;
      } else if (mode === 'bogo') {
        const bogoHave = bogoSave(units);
        heading = units.length <= 1
          ? `Add a 2nd ${noun} — ${BOGO_PCT}% off the cheaper one`
          : bogoHave > 0
            ? `Add one more — another ${BOGO_PCT}% off`
            : `Add another ${noun} — ${BOGO_PCT}% off the cheapest one`;
        note = 'Applied for you at checkout';
      }

      const bogoNow = bogoSave(units);
      const rows = withP.map((w) => {
        const p = w.p;
        const v = w.v;
        const price = v.price;
        const img = v.image || '';
        /* 4.76 — on sale: old price struck through, then the price (owner). */
        const was = v.compare_at_price && v.compare_at_price > price ? v.compare_at_price : 0;
        let sub = was ? `<s>${esc(money(was))}</s> <b>${esc(money(price))}</b>` : esc(money(price));
        if (mode === 'basket') {
          if (w.deal.code === baseCode) sub += ` <span class="ccd-oups-save">· gets you ${basePct}% off: save ${esc(money(w.deal.save))}</span>`;
          /* 4.76 — no "then £X to 10% off" on every row (owner); the heading says it once. */
        } else if (mode === 'bogo') {
          const extra = w.bogo - bogoNow;
          if (extra > 0) {
            const own = Math.round((price * BOGO_PCT) / 100);
            if (own === extra) {
              sub = `<s>${esc(money(price))}</s> <b>${esc(money(price - own))}</b> <span class="ccd-oups-save">· save ${esc(money(extra))}</span>`;
            } else {
              sub += ` <span class="ccd-oups-save">· save ${esc(money(extra))} on your basket</span>`;
            }
          }
        }
        let name = shortName(p.title);
        if (!v.single && v.title && name.toLowerCase().indexOf(String(v.title).toLowerCase()) === -1) name += `, ${v.title}`;
        const url = `${String(p.url || '').split('?')[0]}?variant=${v.id}`;
        const action = `<button type="button" class="ccd-oups-add" data-ccd-add="${v.id}" aria-label="Add ${esc(name)} to basket">${PLUS_ICON}</button>`;
        return (
          `<div class="ccd-oups-row">` +
          `<a class="ccd-oups-img" href="${esc(url)}" tabindex="-1" aria-hidden="true">` +
          (img ? `<img src="${esc(img)}" alt="" width="104" height="104" decoding="async">` : '<span class="ccd-oups-noimg"></span>') +
          `</a>` +
          `<div class="ccd-oups-info">` +
          `<a class="ccd-oups-name" href="${esc(url)}" title="${esc(p.title)}">${esc(name)}</a>` +
          `<span class="ccd-oups-sub">${sub}</span>` +
          `</div>` +
          action +
          `</div>`
        );
      }).join('');

      let track = '';
      if (mode === 'basket') {
        const pct = Math.max(0, Math.min(100, Math.round((real / baseMin) * 100)));
        track = `<div class="ccd-oups-track" aria-hidden="true"><span class="ccd-oups-fill" style="width:${pct}%"></span></div>`;
      }
      /* The top tier bar only shows once the 10% is applied ("10% off
         applied"); before that this box is the one place an offer shows. */
      if (topBar) topBar.hidden = !(baseCode && now.code === baseCode);

      const plain = mode === 'plain' || mode === 'done';
      box.innerHTML =
        `<p class="ccd-oups-heading${plain ? ' ccd-oups-heading--plain' : ''}">${esc(heading)}</p>` +
        (note ? `<p class="ccd-oups-note">${esc(note)}</p>` : '') +
        track +
        `<div class="ccd-oups-list">${rows}</div>`;
      box.hidden = false;
    };
    return true;
  };

  if (!window.customElements) return;
  customElements.whenDefined('cart-drawer').then(() => {
    if (!patch(customElements.get('cart-drawer'))) return;
    const el = document.getElementById('ClaudeCartDrawer');
    if (el && typeof el.paintCtl === 'function') el.paintCtl();
    if (el && typeof el.maybeAutoApply === 'function') el.maybeAutoApply();
  });
})();
